/**
 * NewSageShipToForm — create a NEW Sage ship-to address for a customer, or
 * edit IN PLACE the one this order's draft just created.
 *
 * The single shared create-ship-to affordance: reused by the draft-order
 * ship-to picker and confirmed-order ship-to view (OrderDetailModal), the
 * Edit-in-Sage modal (SageEditModal), and the manual order-create dialog
 * (CreateOrderDialog). With an `orderId` it POSTs to
 * /portal/orders/{id}/sage-ship-to (write linked to the order); without one
 * it POSTs to /portal/customers/{customerNo}/sage-ship-to — the pre-order
 * path. Either way the ship-to CODE is allocated server-side under a
 * per-customer lock (never typed — a re-used code silently overwrites the
 * live Sage address), and a tax schedule is derived from the customer's live
 * Sage geography. The address reaches Sage via Adam's sequential drain
 * (Ship To imports before New Orders / Order Updates, every ~2 min), so an
 * order referencing the new code can be submitted immediately.
 *
 * Edit-in-place (kanban 6a7b740b, AU02226): when this order already has a
 * portal-created ship-to, the form opens with an offer to EDIT that code
 * (PUT /portal/orders/{id}/sage-ship-to/{code}) instead of allocating a
 * second one — the second create used to leave the first code orphaned on
 * the customer in Sage.
 *
 * Validation contract: hard errors (missing/malformed fields) show as a
 * blocking error; overridable holds surface a confirmation whose override is
 * recorded on the sage_writes audit row: address warnings (unrecognized US
 * ZIP, ZIP/state mismatch) and a street/city/state/zip disagreement with
 * Merlin's proposed_new_ship_to (shown field by field, with a one-click
 * "use Merlin's" per row).
 */
import React, { useEffect, useState } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type {
  CustomerSageRep,
  PortalOrderCreatedShipTo,
  SageShipToCreateResponse,
  ShipToProposalFieldDiff,
} from '../../services/portalOrders';
import { portalService } from '../../services/portal';
import { recordAction } from '../../services/guruRecorderBridge';
import type { SalespersonOption } from '../../types/portal';

export interface CreatedShipTo {
  ship_to_code: string;
  ship_to_name: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip_code: string;
  tax_schedule?: string | null;
  tax_schedule_warning?: string | null;
  /** True when an existing portal-created code was edited in place. */
  edited?: boolean;
}

interface Props {
  /** Present: order-scoped create (write linked to the order). */
  orderId?: string;
  /** Required when no orderId (customer-scoped create); shown in the copy. */
  customerNo: string | null | undefined;
  /** Prefill values (e.g. Merlin's proposed new ship-to) — user-editable. */
  initial?: Partial<FormState>;
  /** Label for the cancel button (defaults to "Back to search"). */
  cancelLabel?: string;
  onCreated: (addr: CreatedShipTo) => void;
  onCancel: () => void;
}

interface FormState {
  name: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
  /** Portal country code: 'US' or 'CA'. Drives the postal/state labels and
   *  the postal-code validation rule; sent as ship_to_country_code. */
  country: string;
}

const EMPTY: FormState = { name: '', address1: '', address2: '', address3: '', city: '', state: '', zip: '', country: 'US' };

const input: React.CSSProperties = {
  width: '100%', padding: '6px 8px', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
};
const btn: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db',
  background: '#fff', fontSize: 12.5, cursor: 'pointer',
};
const smallBtn: React.CSSProperties = {
  ...btn, padding: '2px 8px', fontSize: 11.5,
};

/** Map a proposal-diff field to the form field it edits. */
const DIFF_FIELD_TO_FORM: Record<ShipToProposalFieldDiff['field'], keyof FormState> = {
  name: 'name',
  address1: 'address1',
  city: 'city',
  state: 'state',
  zip: 'zip',
};

function formFromCreated(c: PortalOrderCreatedShipTo): FormState {
  return {
    name: c.ship_to_name ?? '',
    address1: c.ship_to_address1 ?? '',
    address2: c.ship_to_address2 ?? '',
    address3: c.ship_to_address3 ?? '',
    city: c.ship_to_city ?? '',
    state: c.ship_to_state ?? '',
    zip: c.ship_to_zip_code ?? '',
    country: c.ship_to_country_code === 'CA' ? 'CA' : 'US',
  };
}

function minutesAgo(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

const NewSageShipToForm: React.FC<Props> = ({ orderId, customerNo, initial, cancelLabel, onCreated, onCancel }) => {
  const [form, setForm] = useState<FormState>(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Overridable address-validation warnings from a held create; any field
  // edit clears them so "Create anyway" always refers to the shown values.
  const [warnings, setWarnings] = useState<string[] | null>(null);
  // Held street/city/state/zip disagreement with Merlin's proposed_new_ship_to
  // (rows with holds=true); cleared on any field edit like the warnings.
  const [proposalDiff, setProposalDiff] = useState<ShipToProposalFieldDiff[] | null>(null);
  // Salesperson to link on the new ship-to. '' = auto (server derives the
  // customer's own rep — today's fail-open behavior). Pre-filled with the
  // customer's rep once the suggestion loads; CSR-adjustable.
  const [salespersonNo, setSalespersonNo] = useState('');
  const salespersonTouched = React.useRef(false);
  const [salespersonOptions, setSalespersonOptions] = useState<SalespersonOption[]>([]);
  const [suggestedRep, setSuggestedRep] = useState<CustomerSageRep | null>(null);
  // Edit-in-place: the ship-to this order's draft already created, the offer
  // to edit it, and whether the form is now editing that code.
  const [createdShipTo, setCreatedShipTo] = useState<PortalOrderCreatedShipTo | null>(null);
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Both fetches fail-open: without options the field simply stays on
    // "auto" and the server-side derivation keeps working as before.
    portalService
      .listSalespersons()
      .then((opts) => {
        if (!cancelled) setSalespersonOptions(opts);
      })
      .catch(() => undefined);
    if (customerNo) {
      portalOrderService
        .getCustomerSageRep(customerNo)
        .then((rep) => {
          if (cancelled) return;
          setSuggestedRep(rep);
          // Pre-select the suggestion unless the CSR already picked a value
          // (including an explicit "Auto").
          if (rep.salesperson_no && !salespersonTouched.current) {
            setSalespersonNo(rep.salesperson_no);
          }
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [customerNo]);

  useEffect(() => {
    // Order-scoped only: a code created FROM THIS ORDER is offered for edit.
    // Fail-open — the offer is a convenience, the create path never waits on it.
    if (!orderId) return;
    let cancelled = false;
    try {
      portalOrderService
        .getCreatedSageShipTo(orderId)
        .then((created) => {
          if (cancelled) return;
          if (created && customerNo && created.customer_no !== customerNo) return;
          setCreatedShipTo(created ?? null);
        })
        .catch(() => undefined);
    } catch {
      // never block the form on the lookup
    }
    return () => {
      cancelled = true;
    };
  }, [orderId, customerNo]);

  const set = (patch: Partial<FormState>) => {
    setWarnings(null);
    setProposalDiff(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  const startEditingCreated = () => {
    if (!createdShipTo) return;
    setEditingCode(createdShipTo.ship_to_code);
    setForm(formFromCreated(createdShipTo));
    if (createdShipTo.salesperson_no && !salespersonTouched.current) {
      setSalespersonNo(createdShipTo.salesperson_no);
    }
    setWarnings(null);
    setProposalDiff(null);
    setError(null);
    recordAction('ship_to_edit_in_place_start', {
      targets: { order_id: orderId, ship_to_code: createdShipTo.ship_to_code },
      outcome: 'open',
    });
  };

  const dismissOffer = () => {
    setOfferDismissed(true);
    recordAction('ship_to_create_another', {
      targets: { order_id: orderId, ship_to_code: createdShipTo?.ship_to_code },
      outcome: 'none',
    });
  };

  const applyProposedValue = (d: ShipToProposalFieldDiff) => {
    const key = DIFF_FIELD_TO_FORM[d.field];
    set({ [key]: d.proposed } as Partial<FormState>);
    recordAction('ship_to_use_merlin_value', {
      targets: { order_id: orderId },
      values: { field: d.field },
      outcome: 'none',
    });
  };

  const handleCreate = async (acknowledge: boolean) => {
    setCreating(true);
    setError(null);
    try {
      const payload = {
        ship_to_name: form.name.trim() || null,
        ship_to_address1: form.address1.trim() || null,
        ship_to_address2: form.address2.trim() || null,
        ship_to_address3: form.address3.trim() || null,
        ship_to_city: form.city.trim() || null,
        ship_to_state: form.state.trim().toUpperCase() || null,
        ship_to_zip_code: form.zip.trim() || null,
        ship_to_country_code: form.country,
        salesperson_no: salespersonNo || null,
        acknowledge_address_warnings: acknowledge,
        acknowledge_proposal_diff: acknowledge,
      };
      let res: SageShipToCreateResponse;
      if (orderId && editingCode) {
        res = await portalOrderService.updateSageShipTo(orderId, editingCode, payload);
      } else if (orderId) {
        res = await portalOrderService.createSageShipTo(orderId, payload);
      } else if (customerNo) {
        res = await portalOrderService.createCustomerSageShipTo(customerNo, payload);
      } else {
        setError('No customer selected to create the ship-to under.');
        return;
      }
      if (res.success && res.ship_to_code) {
        setWarnings(null);
        setProposalDiff(null);
        recordAction(editingCode ? 'ship_to_edit_in_place_saved' : 'ship_to_created', {
          targets: { order_id: orderId, customer_no: customerNo ?? undefined, ship_to_code: res.ship_to_code },
          values: {
            acknowledged: acknowledge ? 1 : 0,
            proposal_diff_fields: (res.proposal_diff ?? []).length,
          },
          outcome: 'request',
        });
        onCreated({
          ship_to_code: res.ship_to_code,
          ship_to_name: form.name.trim(),
          address1: form.address1.trim(),
          address2: form.address2.trim(),
          address3: form.address3.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          zip_code: form.zip.trim(),
          tax_schedule: res.tax_schedule,
          tax_schedule_warning: res.tax_schedule_warning,
          edited: !!editingCode,
        });
      } else if (res.requires_acknowledgment) {
        const held = (res.proposal_diff ?? []).filter((d) => d.holds);
        setWarnings(res.address_warnings?.length ? res.address_warnings : null);
        setProposalDiff(held.length ? held : null);
        recordAction('ship_to_create_held', {
          targets: { order_id: orderId, customer_no: customerNo ?? undefined },
          values: {
            address_warnings: res.address_warnings?.length ?? 0,
            proposal_diff_fields: held.length,
          },
          outcome: 'request',
        });
        if (!res.address_warnings?.length && !held.length) {
          setError(res.error_message || 'Ship-to create needs confirmation');
        }
      } else {
        setError(res.error_message || 'Ship-to create failed');
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally {
      setCreating(false);
    }
  };

  const isCA = form.country === 'CA';
  const held = !!warnings || !!proposalDiff;
  const showOffer = !!createdShipTo && !offerDismissed && !editingCode;
  const createdSummary = createdShipTo
    ? [createdShipTo.ship_to_name, createdShipTo.ship_to_address1, [createdShipTo.ship_to_city, createdShipTo.ship_to_state].filter(Boolean).join(' ')]
        .filter((p) => p && String(p).trim())
        .join(', ')
    : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="new-sage-ship-to-form">
      <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
        {editingCode
          ? `Edit Sage ship-to ${editingCode} for ${customerNo ?? 'this customer'}`
          : `Create a new Sage ship-to for ${customerNo ?? 'this customer'}`}
      </div>
      <div style={{ fontSize: 11.5, color: '#6b7280' }}>
        {editingCode
          ? `Your changes replace the address stored under code ${editingCode} in Sage (syncs within ~2 minutes). No new code is created.`
          : 'The ship-to code is assigned automatically (next free code for this customer). The address syncs to Sage within ~2 minutes — before any order that uses it.'}
      </div>
      {showOffer && createdShipTo && (
        <div
          style={{ fontSize: 12, color: '#1e3a8a', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '8px 10px' }}
          role="status"
          data-testid="ship-to-edit-offer"
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            You already created ship-to {createdShipTo.ship_to_code} for this order
            {createdShipTo.created_at ? ` (${minutesAgo(createdShipTo.created_at)})` : ''}.
          </div>
          {createdSummary && <div style={{ marginBottom: 4 }}>{createdSummary}</div>}
          <div style={{ marginBottom: 6 }}>
            Fixing a typo? Edit {createdShipTo.ship_to_code} instead — creating another address leaves{' '}
            {createdShipTo.ship_to_code} orphaned on this customer in Sage.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ ...smallBtn, background: '#2563eb', borderColor: '#2563eb', color: '#fff' }}
              type="button"
              onClick={startEditingCreated}
              data-testid="ship-to-edit-created-btn"
              data-action="ship_to_edit_in_place_offer_accept"
              data-target-ship-to-code={createdShipTo.ship_to_code}
            >
              Edit {createdShipTo.ship_to_code} instead
            </button>
            <button
              style={smallBtn}
              type="button"
              onClick={dismissOffer}
              data-testid="ship-to-create-another-btn"
              data-action="ship_to_edit_in_place_offer_decline"
              data-target-ship-to-code={createdShipTo.ship_to_code}
            >
              Create another address
            </button>
          </div>
        </div>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#374151' }}>
        <span style={{ minWidth: 54 }}>Country</span>
        <select
          style={{ ...input, flex: 1 }}
          value={form.country}
          onChange={(e) => set({ country: e.target.value })}
          aria-label="New ship-to country"
          data-testid="ship-to-country-select"
        >
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </label>
      <input style={input} placeholder="Name" maxLength={50} value={form.name} onChange={(e) => set({ name: e.target.value })} aria-label="New ship-to name" />
      <input style={input} placeholder="Address line 1" maxLength={40} value={form.address1} onChange={(e) => set({ address1: e.target.value })} aria-label="New ship-to address line 1" />
      <input style={input} placeholder="Address line 2" maxLength={40} value={form.address2} onChange={(e) => set({ address2: e.target.value })} aria-label="New ship-to address line 2" />
      <input style={input} placeholder="Address line 3 (suite, attention, delivery notes…)" maxLength={40} value={form.address3} onChange={(e) => set({ address3: e.target.value })} aria-label="New ship-to address line 3" />
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={{ ...input, flex: 2 }} placeholder="City" maxLength={30} value={form.city} onChange={(e) => set({ city: e.target.value })} aria-label="New ship-to city" />
        <input style={{ ...input, width: 60, flex: 'none' }} placeholder={isCA ? 'Prov' : 'ST'} maxLength={2} value={form.state} onChange={(e) => set({ state: e.target.value })} aria-label={isCA ? 'New ship-to province' : 'New ship-to state'} />
        <input style={{ ...input, flex: 1 }} placeholder={isCA ? 'A1A 1A1' : 'ZIP'} maxLength={10} value={form.zip} onChange={(e) => set({ zip: e.target.value })} aria-label={isCA ? 'New ship-to postal code' : 'New ship-to zip'} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#374151' }}>
        <span style={{ minWidth: 54 }}>Rep</span>
        <select
          style={{ ...input, flex: 1 }}
          value={salespersonNo}
          onChange={(e) => {
            salespersonTouched.current = true;
            setWarnings(null);
            setProposalDiff(null);
            setSalespersonNo(e.target.value);
          }}
          aria-label="New ship-to salesperson"
          data-testid="ship-to-salesperson-select"
        >
          <option value="">Auto — customer's rep</option>
          {suggestedRep?.salesperson_no &&
            !salespersonOptions.some((o) => o.salesperson_no === suggestedRep.salesperson_no) && (
              <option value={suggestedRep.salesperson_no}>
                {suggestedRep.salesperson_name || suggestedRep.salesperson_no} ({suggestedRep.salesperson_no})
              </option>
            )}
          {/* The rep carried by the record being edited stays selectable even
              when it is absent from the active-rep list (inactive rep), so an
              address edit never silently drops the linked rep. */}
          {editingCode && salespersonNo &&
            salespersonNo !== suggestedRep?.salesperson_no &&
            !salespersonOptions.some((o) => o.salesperson_no === salespersonNo) && (
              <option value={salespersonNo}>{salespersonNo} (current)</option>
            )}
          {salespersonOptions.map((o) => (
            <option key={o.salesperson_no} value={o.salesperson_no}>
              {o.name} ({o.salesperson_no})
            </option>
          ))}
        </select>
      </label>
      {suggestedRep?.salesperson_no && (
        <div style={{ fontSize: 11.5, color: '#6b7280' }} data-testid="ship-to-salesperson-hint">
          Suggested: {suggestedRep.salesperson_name || suggestedRep.salesperson_no} — {suggestedRep.customer_no}'s
          rep in Sage. Adjust if this address belongs to a different rep.
        </div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: '#dc2626' }} role="alert">⚠ {error}</div>
      )}
      {warnings && (
        <div
          style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px' }}
          role="alert"
          data-testid="ship-to-address-warnings"
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Please double-check this address:</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <div style={{ marginTop: 4 }}>
            Fix the address above, or {editingCode ? 'save' : 'create'} it anyway if it is genuinely correct
            (your confirmation is recorded).
          </div>
        </div>
      )}
      {proposalDiff && (
        <div
          style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px' }}
          role="alert"
          data-testid="ship-to-proposal-diff"
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>This differs from what Merlin read on the PO:</div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
            <tbody>
              {proposalDiff.map((d) => (
                <tr key={d.field} data-testid={`ship-to-proposal-diff-${d.field}`}>
                  <td style={{ padding: '2px 6px 2px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{d.label}</td>
                  <td style={{ padding: '2px 6px', verticalAlign: 'top' }}>
                    <div>Merlin read: <span style={{ fontFamily: 'monospace' }}>{d.proposed}</span></div>
                    <div>You entered: <span style={{ fontFamily: 'monospace' }}>{d.entered || '(blank)'}</span></div>
                  </td>
                  <td style={{ padding: '2px 0 2px 6px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <button
                      style={smallBtn}
                      type="button"
                      onClick={() => applyProposedValue(d)}
                      data-testid={`ship-to-use-proposed-${d.field}`}
                      data-action="ship_to_use_merlin_value"
                    >
                      Use Merlin's
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 4 }}>
            A retyped street ships to the wrong place. Take Merlin's value, fix the field above, or{' '}
            {editingCode ? 'save' : 'create'} it anyway if the PO really says what you entered (your confirmation is recorded).
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btn} type="button" onClick={onCancel}>{cancelLabel ?? 'Back to search'}</button>
        {held ? (
          <button
            style={{ ...btn, background: '#d97706', borderColor: '#d97706', color: '#fff', opacity: creating ? 0.5 : 1 }}
            type="button"
            disabled={creating}
            onClick={() => void handleCreate(true)}
            data-testid="create-sage-ship-to-anyway-btn"
            data-action={editingCode ? 'ship_to_save_anyway' : 'ship_to_create_anyway'}
          >
            {creating ? (editingCode ? 'Saving…' : 'Creating…') : editingCode ? 'Save anyway' : 'Create anyway'}
          </button>
        ) : (
          <button
            style={{ ...btn, background: '#2563eb', borderColor: '#2563eb', color: '#fff', opacity: creating || !form.address1.trim() ? 0.5 : 1 }}
            type="button"
            disabled={creating || !form.address1.trim()}
            onClick={() => void handleCreate(false)}
            data-testid="create-sage-ship-to-btn"
            data-action={editingCode ? 'ship_to_save_in_place' : 'ship_to_create'}
          >
            {creating
              ? (editingCode ? 'Saving…' : 'Creating…')
              : editingCode
                ? `Save changes to ${editingCode} in Sage`
                : 'Create in Sage & use'}
          </button>
        )}
      </div>
    </div>
  );
};

export default NewSageShipToForm;
